import React from 'react';
import {
  Typography,
  Paper,
  Grid,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Avatar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dashboard as DashboardIcon,
  Edit,
  Create,
  TrendingUp,
  CheckCircle
} from '@material-ui/icons';
import { useAuth } from '../context/ApiKeyContext';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../context/LanguageContext';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  welcomeCard: {
    background: 'var(--ikaro-gradient-banner)',
    color: 'var(--ikaro-text-white)',
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2),
  },
  welcomeContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  avatar: {
    width: theme.spacing(8),
    height: theme.spacing(8),
    backgroundColor: 'var(--ikaro-white-semi)',
    color: 'var(--ikaro-text-dark)',
    fontSize: '2rem',
  },
  quickActionCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  quickActionIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(1),
  },
  quickActionContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  quickActionActions: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(2),
  },
  statsCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  statsNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  recentActivityCard: {
    height: '100%',
  },
  activityList: {
    maxHeight: 300,
    overflow: 'auto',
  },
  activityItem: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  chipSuccess: {
    backgroundColor: 'var(--ikaro-status-success)',
    color: 'var(--ikaro-text-white)',
  },
  chipPending: {
    backgroundColor: 'var(--ikaro-status-warning)',
    color: 'var(--ikaro-text-white)',
  },
  sectionTitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: 'bold',
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const { user } = useAuth();
  const history = useHistory();
  const { t } = useTranslation('dashboard');
  const { formatDate } = useLanguage();

  const handleNavigate = (path) => {
    history.push(path);
  };

  const getUserInitials = () => {
    if (user && user.name) {
      return user.name.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getUserDisplayName = () => {
    if (user && user.name) {
      return user.name;
    }
    if (user && user.email) {
      return user.email.split('@')[0];
    }
    return t('defaultUser');
  };

  const getCompanyName = () => {
    if (user && user.company_name) {
      return user.company_name;
    }
    return null;
  };

  const getTenantName = () => {
    if (user && user.tenant_name) {
      return user.tenant_name;
    }
    return null;
  };

  const quickActions = [
    {
      title: t('tools.templateEditor.title'),
      description: t('tools.templateEditor.description'),
      icon: <Edit className={classes.quickActionIcon} color="primary" />,
      path: '/editor-templates',
      color: 'primary',
    },
    {
      title: t('tools.contestation.title'),
      description: t('tools.contestation.description'),
      icon: <Create className={classes.quickActionIcon} color="secondary" />,
      path: '/contestacoes',
      color: 'secondary',
    },
  ];

  const stats = [
    {
      number: '2',
      label: t('stats.availableTools'),
      icon: <DashboardIcon />,
    },
    {
      number: '99.98%',
      label: t('stats.availability'),
      icon: <TrendingUp />,
    },
    {
      number: t('stats.active'),
      label: t('stats.accountStatus'),
      icon: <CheckCircle />,
    },
  ];

  return (
    <div className={classes.root}>
      {/* Welcome Card */}
      <Paper elevation={3} className={classes.welcomeCard}>
        <div className={classes.welcomeContent}>
          <div className={classes.userInfo}>
            <Avatar className={classes.avatar}>
              {getUserInitials()}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom style={{ color: 'var(--ikaro-text-white)' }}>
                {t('welcome', { name: getUserDisplayName() })}
              </Typography>
              <Typography variant="subtitle1" style={{ color: 'var(--ikaro-text-white)' }}>
                {t('ready')}
              </Typography>
            </Box>
          </div>
          <Box>
            <Typography variant="h6" align="right" style={{ color: 'var(--ikaro-text-white)' }}>
              {formatDate(new Date(), { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Typography>
            {(getCompanyName() || getTenantName()) && (
              <Typography variant="body2" align="right" style={{ 
                color: 'var(--ikaro-text-white)', 
                marginTop: '4px',
                opacity: 0.9 
              }}>
                {getCompanyName() && (
                  <span style={{ fontWeight: 600 }}>{getCompanyName()}</span>
                )}
                {getCompanyName() && getTenantName() && (
                  <span style={{ margin: '0 8px', opacity: 0.7 }}>→</span>
                )}
                {getTenantName() && (
                  <span style={{ fontWeight: 400 }}>{getTenantName()}</span>
                )}
              </Typography>
            )}
          </Box>
        </div>
      </Paper>

      {/* Quick Actions */}
      <Typography variant="h5" className={classes.sectionTitle}>
        {t('quickActions')}
      </Typography>
      <Grid container spacing={3} style={{ marginBottom: 32 }}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card className={classes.quickActionCard}>
              <CardContent className={classes.quickActionContent}>
                {action.icon}
                <Typography variant="h6" component="h2" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {action.description}
                </Typography>
              </CardContent>
              <CardActions className={classes.quickActionActions}>
                <Button
                  size="large"
                  color={action.color}
                  variant="contained"
                  onClick={() => handleNavigate(action.path)}
                >
                  {t('access')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Stats and Recent Activity */}
      <Grid container spacing={3}>
        {/* Stats */}
        <Grid item xs={12} md={12}>
          <Typography variant="h5" className={classes.sectionTitle}>
            {t('statistics')}
          </Typography>
          <Grid container spacing={2}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <Box display="flex" justifyContent="center" mb={1}>
                      {React.cloneElement(stat.icon, { 
                        style: { fontSize: '2rem', color: 'var(--ikaro-orange-primary)' } 
                      })}
                    </Box>
                    <Typography variant="h4" className={classes.statsNumber}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" className={classes.statsLabel}>
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Recent Activity */}
        {/* <Grid item xs={12} md={4}>
          <Card className={classes.recentActivityCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('recentActivities')}
              </Typography>
              <Divider style={{ marginBottom: 16 }} />
              <List className={classes.activityList}>
                {recentActivities.map((activity, index) => (
                  <ListItem key={index} className={classes.activityItem}>
                    <ListItemIcon>
                      {React.cloneElement(activity.icon, { 
                        style: { color: activity.status === 'success' ? 'var(--ikaro-status-success)' : 'var(--ikaro-status-warning)' } 
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.text}
                      secondary={activity.time}
                    />
                    <Chip
                      size="small"
                      label={activity.status === 'success' ? t('activities.statusOk') : t('activities.statusPending')}
                      className={activity.status === 'success' ? classes.chipSuccess : classes.chipPending}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>
    </div>
  );
};

export default Dashboard; 